import React, { useState, useEffect } from 'react';
import styles from './ConsentManager.module.css';
import Logger from '../../services/logger';
import { PUBLIC_CONTACT_EMAIL } from '../../constants/landingPublicContact';

interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  location: boolean;
  notifications: boolean;
  dataSharing: boolean;
  personalization: boolean;
}

interface ConsentManagerProps {
  onConsentChange?: (consent: ConsentPreferences) => void;
  showModal?: boolean;
  onClose?: () => void;
  className?: string;
}

const ConsentManager: React.FC<ConsentManagerProps> = ({ 
  onConsentChange, 
  showModal = false,
  onClose,
  className = '' 
}) => {
  const [consent, setConsent] = useState<ConsentPreferences>({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    location: false,
    notifications: false,
    dataSharing: false,
    personalization: false
  });

  const [showConsentModal, setShowConsentModal] = useState(showModal);
  const [hasLoadedConsent, setHasLoadedConsent] = useState(false);

  // Load saved consent preferences on component mount
  useEffect(() => {
    const loadConsent = () => {
      try {
        const savedConsent = localStorage.getItem('tranzit-consent-preferences');
        if (savedConsent) {
          const parsedConsent = JSON.parse(savedConsent);
          setConsent(prev => ({ ...prev, ...parsedConsent }));
        }
        setHasLoadedConsent(true);
      } catch (error) {
        Logger.error('Error loading consent preferences:', error instanceof Error ? error : new Error(String(error)), { error });
        setHasLoadedConsent(true);
      }
    };

    loadConsent();
  }, []);

  // Save consent preferences to localStorage
  const saveConsent = (newConsent: ConsentPreferences) => {
    try {
      localStorage.setItem('tranzit-consent-preferences', JSON.stringify(newConsent));
      setConsent(newConsent);
      if (onConsentChange) {
        onConsentChange(newConsent);
      }
    } catch (error) {
      Logger.error('Error saving consent preferences:', error instanceof Error ? error : new Error(String(error)), { error });
    }
  };

  const handleConsentChange = (key: keyof ConsentPreferences, value: boolean) => {
    const newConsent = { ...consent, [key]: value };
    saveConsent(newConsent);
  };

  const handleSaveAll = () => {
    saveConsent(consent);
    if (onClose) {
      onClose();
    }
    setShowConsentModal(false);
  };

  const handleAcceptAll = () => {
    const acceptAllConsent: ConsentPreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      location: true,
      notifications: true,
      dataSharing: true,
      personalization: true
    };
    saveConsent(acceptAllConsent);
    if (onClose) {
      onClose();
    }
    setShowConsentModal(false);
  };

  const handleRejectAll = () => {
    const rejectAllConsent: ConsentPreferences = {
      essential: true, // Always required
      analytics: false,
      marketing: false,
      location: false,
      notifications: false,
      dataSharing: false,
      personalization: false
    };
    saveConsent(rejectAllConsent);
    if (onClose) {
      onClose();
    }
    setShowConsentModal(false);
  };

  const openConsentModal = () => {
    setShowConsentModal(true);
  };

  const closeConsentModal = () => {
    setShowConsentModal(false);
    if (onClose) {
      onClose();
    }
  };

  // Don't render until consent is loaded
  if (!hasLoadedConsent) {
    return null;
  }

  return (
    <>
      <div className={className}>
        <a 
          href="#"
          onClick={(e) => { e.preventDefault(); openConsentModal(); }}
          style={{ 
            color: '#fff', 
            textDecoration: 'none', 
            fontSize: '0.95rem',
            lineHeight: '1'
          }}
        >
          Privacy Settings
        </a>
      </div>

      {showConsentModal && (
        <div className={styles.modalOverlay} onClick={closeConsentModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Privacy & Consent Settings</h2>
              <button 
                className={styles.closeButton}
                onClick={closeConsentModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <p className={styles.description}>
                We respect your privacy and give you control over how your data is used. 
                You can customize your preferences below. Essential cookies are required for the platform to function.
              </p>

              <div className={styles.consentSection}>
                <h3>Essential Services</h3>
                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Essential Cookies</h4>
                    <p>Required for basic platform functionality, authentication, and security.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.essential}
                      disabled
                      className={styles.checkbox}
                    />
                    <span className={styles.required}>Required</span>
                  </div>
                </div>
              </div>

              <div className={styles.consentSection}>
                <h3>Optional Services</h3>
                
                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Analytics & Performance</h4>
                    <p>Help us understand how you use our platform to improve performance and user experience.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => handleConsentChange('analytics', e.target.checked)}
                      className={styles.checkbox}
                    />
                  </div>
                </div>

                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Location Services</h4>
                    <p>Enable location-based features like load matching and route optimization.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.location}
                      onChange={(e) => handleConsentChange('location', e.target.checked)}
                      className={styles.checkbox}
                    />
                  </div>
                </div>

                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Notifications</h4>
                    <p>Receive updates about loads, shipments, and important platform notifications.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.notifications}
                      onChange={(e) => handleConsentChange('notifications', e.target.checked)}
                      className={styles.checkbox}
                    />
                  </div>
                </div>

                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Data Sharing</h4>
                    <p>Allow sharing of your profile information with other users for load matching and partnerships.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.dataSharing}
                      onChange={(e) => handleConsentChange('dataSharing', e.target.checked)}
                      className={styles.checkbox}
                    />
                  </div>
                </div>

                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Personalization</h4>
                    <p>Customize your experience with personalized recommendations and interface preferences.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.personalization}
                      onChange={(e) => handleConsentChange('personalization', e.target.checked)}
                      className={styles.checkbox}
                    />
                  </div>
                </div>

                <div className={styles.consentItem}>
                  <div className={styles.consentInfo}>
                    <h4>Marketing Communications</h4>
                    <p>Receive promotional emails, newsletters, and updates about new features.</p>
                  </div>
                  <div className={styles.consentToggle}>
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                      className={styles.checkbox}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.dataRights}>
                <h3>Your Data Rights</h3>
                <p>You have the right to:</p>
                <ul>
                  <li>Access your personal data</li>
                  <li>Correct inaccurate information</li>
                  <li>Delete your data</li>
                  <li>Export your data</li>
                  <li>Withdraw consent at any time</li>
                  <li>Object to data processing</li>
                </ul>
                <p>
                  To exercise these rights or for questions about your data, 
                  contact us at{' '}
                  <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`}>{PUBLIC_CONTACT_EMAIL}</a>
                </p>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.rejectButton}
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button 
                className={styles.saveButton}
                onClick={handleSaveAll}
              >
                Save Preferences
              </button>
              <button 
                className={styles.acceptButton}
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentManager;
